import { z } from 'zod';

/**
 * VC-1 identity wire contracts.
 *
 * These deliberately expose less than the domain holds. `externalSubject` — the
 * Cognito `sub` — never crosses the wire: it is an internal join key, and
 * publishing it would make a future identity-provider migration a breaking API
 * change for every client (§14, §36).
 */
export const PlatformRoleSchema = z.enum(['PLATFORM_OWNER', 'PLATFORM_ADMIN']);

export const OrganizationRoleSchema = z.enum([
  'ORGANIZATION_OWNER',
  'ORGANIZATION_ADMIN',
  'ORGANIZATION_DEVELOPER',
  'ORGANIZATION_ANALYST',
  'ORGANIZATION_READ_ONLY',
]);

/** Roles an invitation may confer. OWNER is grantable only by an owner. */
export const InvitableRoleSchema = OrganizationRoleSchema;

export const MembershipStatusSchema = z.enum(['INVITED', 'ACTIVE', 'SUSPENDED', 'REMOVED']);
export const InvitationStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'EXPIRED', 'REVOKED']);
export const OrganizationStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'ARCHIVED']);
export const UserStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'DISABLED']);

export const meResponseSchema = z
  .object({
    user: z.object({
      id: z.string(),
      email: z.email(),
      display_name: z.string(),
      status: UserStatusSchema,
      email_verified: z.boolean(),
    }),
    platform_roles: z.array(PlatformRoleSchema),
    organizations: z.array(
      z.object({
        organization_id: z.string(),
        display_name: z.string(),
        slug: z.string(),
        role: OrganizationRoleSchema,
        membership_status: MembershipStatusSchema,
      }),
    ),
  })
  .meta({
    id: 'MeResponse',
    title: 'MeResponse',
    description: 'The authenticated user, their platform roles and their organization memberships.',
  });

export type MeResponse = z.infer<typeof meResponseSchema>;

export const organizationSchema = z
  .object({
    id: z.string(),
    display_name: z.string(),
    slug: z.string(),
    status: OrganizationStatusSchema,
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
  })
  .meta({ id: 'Organization', title: 'Organization', description: 'A vendor organization.' });

export type OrganizationResource = z.infer<typeof organizationSchema>;

export const createOrganizationRequestSchema = z
  .object({
    display_name: z.string().min(1).max(200),
    // Optional. Derived from the display name when omitted. Internal fields
    // (id, status, timestamps) are not accepted at all (§16).
    slug: z
      .string()
      .min(2)
      .max(48)
      .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/)
      .optional(),
  })
  .meta({ id: 'CreateOrganizationRequest', title: 'CreateOrganizationRequest' });

export const updateOrganizationRequestSchema = z
  .object({
    display_name: z.string().min(1).max(200).optional(),
    status: OrganizationStatusSchema.optional(),
  })
  .meta({ id: 'UpdateOrganizationRequest', title: 'UpdateOrganizationRequest' });

export const memberSchema = z
  .object({
    membership_id: z.string(),
    user_id: z.string(),
    email: z.email(),
    display_name: z.string(),
    role: OrganizationRoleSchema,
    status: MembershipStatusSchema,
    joined_at: z.iso.datetime().nullable(),
  })
  .meta({ id: 'OrganizationMember', title: 'OrganizationMember' });

export type MemberResource = z.infer<typeof memberSchema>;

export const invitationSchema = z
  .object({
    invitation_id: z.string(),
    email: z.email(),
    role: OrganizationRoleSchema,
    status: InvitationStatusSchema,
    invited_by_user_id: z.string(),
    created_at: z.iso.datetime(),
    expires_at: z.iso.datetime(),
  })
  .meta({
    id: 'OrganizationInvitation',
    title: 'OrganizationInvitation',
    description: 'An invitation to join an organization. Carries no token or secret.',
  });

export type InvitationResource = z.infer<typeof invitationSchema>;

export const createInvitationRequestSchema = z
  .object({
    email: z.email().max(320),
    role: InvitableRoleSchema,
  })
  .meta({ id: 'CreateInvitationRequest', title: 'CreateInvitationRequest' });

export const updateMemberRequestSchema = z
  .object({ role: OrganizationRoleSchema })
  .meta({ id: 'UpdateMemberRequest', title: 'UpdateMemberRequest' });

/**
 * A named user as platform administration sees them.
 *
 * Deliberately narrower than the domain `User`: `externalSubject` is the Cognito
 * `sub`, an identifier for a credential rather than for a person, and nothing in
 * the console needs it. Leaving it out of the wire type means no future handler
 * can leak it by forgetting to strip it (§38).
 */
export const platformUserSchema = z
  .object({
    id: z.string(),
    email: z.email(),
    display_name: z.string(),
    status: UserStatusSchema,
    email_verified: z.boolean(),
    platform_roles: z.array(PlatformRoleSchema),
    created_at: z.iso.datetime(),
    last_seen_at: z.iso.datetime().nullable(),
  })
  .meta({ id: 'PlatformUser', description: 'A named user of the Verydia Control platform.' });

export type PlatformUserResource = z.infer<typeof platformUserSchema>;

export const grantPlatformRoleRequestSchema = z
  .object({ role: PlatformRoleSchema })
  .meta({ id: 'GrantPlatformRoleRequest' });

export const seedOrganizationOwnerRequestSchema = z
  .object({ email: z.email().max(320) })
  .meta({ id: 'SeedOrganizationOwnerRequest' });

export const seedOrganizationOwnerResponseSchema = z
  .object({
    outcome: z.enum(['membership_created', 'invitation_sent']),
    email: z.email(),
  })
  .meta({
    id: 'SeedOrganizationOwnerResponse',
    description:
      'Whether the person became a member immediately or was invited to join at first sign-in.',
  });

export const auditEventSchema = z
  .object({
    id: z.string(),
    occurred_at: z.iso.datetime(),
    actor_type: z.enum(['USER', 'API_CREDENTIAL', 'SYSTEM']),
    actor_id: z.string().nullable(),
    action: z.string(),
    resource_type: z.string(),
    resource_id: z.string().nullable(),
    organization_id: z.string().nullable(),
    request_id: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()),
  })
  .meta({ id: 'AuditEvent', title: 'AuditEvent' });

export type AuditEventResource = z.infer<typeof auditEventSchema>;

/* -------------------------------- audit actions ----------------------------- */

/**
 * Every action that can appear in an audit record.
 *
 * It lives in contracts because it is wire data: it is what `action` carries on
 * an `AuditEventResource`, so the console reads it too. Keeping it in the
 * application layer forced the console either to import across a boundary it
 * must not cross, or to keep a second copy — and a second copy of a closed enum
 * is a copy that drifts.
 */
export const AuditActions = {
  PLATFORM_OWNER_BOOTSTRAPPED: 'PLATFORM_OWNER_BOOTSTRAPPED',
  USER_FIRST_SEEN: 'USER_FIRST_SEEN',
  USER_SUSPENDED: 'USER_SUSPENDED',
  USER_REACTIVATED: 'USER_REACTIVATED',
  VENDOR_ORGANIZATION_CREATED: 'VENDOR_ORGANIZATION_CREATED',
  VENDOR_ORGANIZATION_UPDATED: 'VENDOR_ORGANIZATION_UPDATED',
  /** A platform operator handed over the first ownership of an organization. */
  ORGANIZATION_OWNER_SEEDED: 'ORGANIZATION_OWNER_SEEDED',
  ORGANIZATION_INVITATION_CREATED: 'ORGANIZATION_INVITATION_CREATED',
  ORGANIZATION_INVITATION_REVOKED: 'ORGANIZATION_INVITATION_REVOKED',
  ORGANIZATION_MEMBERSHIP_ACTIVATED: 'ORGANIZATION_MEMBERSHIP_ACTIVATED',
  ORGANIZATION_MEMBER_ROLE_CHANGED: 'ORGANIZATION_MEMBER_ROLE_CHANGED',
  ORGANIZATION_MEMBER_SUSPENDED: 'ORGANIZATION_MEMBER_SUSPENDED',
  ORGANIZATION_MEMBER_REMOVED: 'ORGANIZATION_MEMBER_REMOVED',

  // Catalog (VC-2).
  PRODUCT_CREATED: 'PRODUCT_CREATED',
  PRODUCT_UPDATED: 'PRODUCT_UPDATED',
  PRODUCT_ARCHIVED: 'PRODUCT_ARCHIVED',
  PRODUCT_ENVIRONMENT_CREATED: 'PRODUCT_ENVIRONMENT_CREATED',
  PRODUCT_ENVIRONMENT_UPDATED: 'PRODUCT_ENVIRONMENT_UPDATED',
  PRODUCT_ENVIRONMENT_ARCHIVED: 'PRODUCT_ENVIRONMENT_ARCHIVED',
  LICENSED_PRODUCT_PLAN_CREATED: 'LICENSED_PRODUCT_PLAN_CREATED',
  LICENSED_PRODUCT_PLAN_UPDATED: 'LICENSED_PRODUCT_PLAN_UPDATED',
  LICENSED_PRODUCT_PLAN_ACTIVATED: 'LICENSED_PRODUCT_PLAN_ACTIVATED',
  LICENSED_PRODUCT_PLAN_ARCHIVED: 'LICENSED_PRODUCT_PLAN_ARCHIVED',

  // Customers (VC-3).
  CUSTOMER_CREATED: 'CUSTOMER_CREATED',
  CUSTOMER_UPDATED: 'CUSTOMER_UPDATED',
  CUSTOMER_ARCHIVED: 'CUSTOMER_ARCHIVED',
} as const;

export type AuditAction = (typeof AuditActions)[keyof typeof AuditActions];
